import { initComposerCatalogTrackPicker } from './ComposerCatalogTrackPicker.js';

const REPERTOIRE_MODE = 'catalog_and_upload_v1';
const PREVIEW_DELAY = 250;

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

export function initComposerCatalogForm(root = document) {
  root.querySelectorAll('[data-composer-catalog-form]').forEach(form => {
    if (form.dataset.catalogInitialized) return;
    form.dataset.catalogInitialized = 'true';
    const formElement = form.matches('form') ? form : form.closest('form');
    form.querySelectorAll('[data-catalog-track-picker]').forEach(initComposerCatalogTrackPicker);

    const rights = [...form.querySelectorAll('input[type="checkbox"][data-instore-right]')];
    const bulk = form.querySelector('#instore_all_rights');
    const participation = form.querySelector('[data-instore-participation-section]');
    const frameworkConsent = form.querySelector('#instore_consent');
    const catalogConsent = form.querySelector('#instore_catalog_consent');
    const repertoireContext = form.querySelector('[name="instore_participation[repertoire_context]"]');
    const preview = participation?.querySelector('[data-repertoire-preview]');
    const previewStatus = preview?.querySelector('[data-repertoire-preview-status]');
    const previewCount = preview?.querySelector('[data-repertoire-preview-count]');
    const previewExcluded = preview?.querySelector('[data-repertoire-preview-excluded]');
    const previewTracks = preview?.querySelector('[data-repertoire-preview-tracks]');
    const download = preview?.querySelector('[data-repertoire-download]');
    const submit = form.querySelector('[data-instore-submit]');
    const value = field => form.querySelector(`#composer_catalog_pro_statement_${field}`)?.value;
    const previewUrl = preview?.dataset.previewUrl || participation?.dataset.repertoirePreviewUrl || form.dataset.repertoirePreviewUrl;
    const downloadUrl = preview?.dataset.downloadUrl || participation?.dataset.repertoireDownloadUrl || form.dataset.repertoireDownloadUrl || previewUrl;
    const repertoireEnabled = participation?.dataset.repertoireMode === REPERTOIRE_MODE && !!previewUrl;
    let previewTimer;
    let previewRequest;
    let previewSequence = 0;
    let previewState = repertoireEnabled ? 'idle' : 'disabled';
    let latestPreviewContext = '';

    const setPreviewMessage = message => {
      if (previewStatus) previewStatus.textContent = message || '';
    };

    const clearPreviewResult = () => {
      if (previewCount) previewCount.textContent = '';
      if (previewExcluded) previewExcluded.textContent = '';
      previewTracks?.replaceChildren();
      latestPreviewContext = '';
      if (repertoireContext) repertoireContext.value = '';
    };

    const abortPreview = () => {
      clearTimeout(previewTimer);
      previewRequest?.abort();
      previewRequest = undefined;
      previewSequence += 1;
    };

    const setPreviewPending = () => {
      previewState = 'pending';
      clearPreviewResult();
      setPreviewMessage(preview?.dataset.pendingMessage || '');
      if (preview) preview.hidden = false;
      if (catalogConsent) catalogConsent.disabled = true;
      if (submit) submit.disabled = !!catalogConsent?.checked;
    };

    const setPreviewError = message => {
      previewState = 'error';
      clearPreviewResult();
      setPreviewMessage(message || preview?.dataset.errorMessage || '');
      if (preview) preview.hidden = false;
      if (catalogConsent) {
        catalogConsent.checked = false;
        catalogConsent.disabled = true;
      }
      if (submit) submit.disabled = false;
    };

    const setPreviewReady = data => {
      previewState = 'ready';
      latestPreviewContext = String(data.preview_context || '');
      if (previewCount) previewCount.textContent = String(data.count ?? 0);
      if (previewExcluded) previewExcluded.textContent = String(data.excluded_count ?? 0);
      if (previewTracks) {
        previewTracks.replaceChildren();
        (Array.isArray(data.tracks) ? data.tracks : []).slice(0, 5).forEach(track => {
          const item = document.createElement('li');
          item.textContent = `${track.id} · ${track.title}`;
          previewTracks.append(item);
        });
      }
      setPreviewMessage(preview?.dataset.readyMessage || '');
      if (preview) preview.hidden = false;
      if (catalogConsent) catalogConsent.disabled = !frameworkConsent?.checked;
      if (catalogConsent?.checked && repertoireContext) repertoireContext.value = latestPreviewContext;
      if (submit) submit.disabled = false;
    };

    const requestPreview = async () => {
      if (!repertoireEnabled) return;
      const sequence = ++previewSequence;
      previewRequest?.abort();
      previewRequest = new AbortController();
      setPreviewPending();
      try {
        const response = await fetch(previewUrl, {
          method: 'POST',
          body: new FormData(formElement),
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
          signal: previewRequest.signal
        });
        const data = await response.json().catch(() => ({}));
        if (sequence !== previewSequence) return;
        if (!response.ok || response.redirected || !data || typeof data.preview_context !== 'string' || !data.preview_context.trim()) {
          throw new Error(data.error || preview?.dataset.errorMessage || 'Preview unavailable');
        }
        setPreviewReady(data);
      } catch (error) {
        if (sequence !== previewSequence || error.name === 'AbortError') return;
        setPreviewError(error.message);
      } finally {
        if (sequence === previewSequence) previewRequest = undefined;
      }
    };

    const schedulePreview = () => {
      if (!repertoireEnabled) return;
      abortPreview();
      setPreviewPending();
      previewTimer = setTimeout(requestPreview, PREVIEW_DELAY);
    };

    const updateSubmitLabel = (eligible, consent, decision) => {
      if (!submit) return;
      submit.textContent = eligible && consent?.checked ? submit.dataset.participationLabel : eligible && decision?.value ? submit.dataset.responseLabel : submit.dataset.informationLabel;
    };

    const update = event => {
      if (bulk) {
        const checked = rights.filter(input => input.checked).length;
        bulk.checked = checked === rights.length;
        bulk.indeterminate = checked > 0 && checked < rights.length;
      }
      const visible = {
        'pro-society': !!value('pro_status') && value('pro_status') !== 'not_a_pro_member',
        'direct-deal': value('pro_status') === 'pro_member_with_direct_deal_possible',
        countries: value('territory_scope') === 'custom',
        'selected-tracks': value('rights_scope') === 'selected_tracks',
        'end-date': value('term_scope') === 'until_date',
        mandates: value('neighboring_rights_mandate_status') !== 'none_declared'
      };
      form.querySelectorAll('[data-catalog-conditional]').forEach(section => {
        section.hidden = !visible[section.dataset.catalogConditional];
        if (section.dataset.catalogConditional === 'selected-tracks' && !section.hidden) section.closest('details').open = true;
      });

      const eligible = !participation || value('pro_status') === 'not_a_pro_member';
      const decision = form.querySelector('#instore_decision');
      if (participation) {
        participation.hidden = !eligible;
        participation.querySelectorAll('input, select, textarea').forEach(input => { input.disabled = !eligible; });
      }
      if (!eligible && frameworkConsent) frameworkConsent.checked = false;
      if (!eligible || decision?.value === 'needs_review' || decision?.value === 'declined') {
        if (catalogConsent) catalogConsent.checked = false;
        if (repertoireContext) repertoireContext.value = '';
        if (repertoireEnabled) {
          abortPreview();
          previewState = 'idle';
        }
      }
      if (event?.target === frameworkConsent && frameworkConsent.checked && decision) decision.value = '';
      if (event?.target === decision && decision.value && frameworkConsent) frameworkConsent.checked = false;
      if (event?.target === frameworkConsent && !frameworkConsent.checked) {
        if (catalogConsent) catalogConsent.checked = false;
        if (repertoireContext) repertoireContext.value = '';
        abortPreview();
        previewState = 'idle';
      }
      if (event?.target === catalogConsent && catalogConsent.checked && previewState === 'ready' && repertoireContext) {
        repertoireContext.value = latestPreviewContext;
      }
      if (catalogConsent) catalogConsent.disabled = !eligible || !frameworkConsent?.checked || previewState !== 'ready';
      if (!frameworkConsent?.checked && catalogConsent) catalogConsent.checked = false;
      updateSubmitLabel(eligible, frameworkConsent, decision);
      if (submit) submit.disabled = !!catalogConsent?.checked && (previewState !== 'ready' || !repertoireContext?.value);

      const rightsChanged = event?.target?.name?.startsWith('composer_catalog_pro_statement[');
      if (rightsChanged && repertoireEnabled && eligible) schedulePreview();
      if (event?.target === catalogConsent && !catalogConsent.checked && repertoireContext) repertoireContext.value = '';
    };

    if (bulk) {
      const bulkEnabled = repertoireEnabled || form.dataset.catalogBulkRights === 'true';
      form.querySelector('#instore_bulk_control').hidden = !bulkEnabled;
      bulk.disabled = !bulkEnabled;
      if (bulkEnabled) {
        bulk.addEventListener('change', () => {
          rights.forEach(input => { input.checked = bulk.checked; });
          update();
          schedulePreview();
        });
      }
    }

    if (download && repertoireEnabled) {
      download.addEventListener('click', async () => {
        download.disabled = true;
        download.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(downloadUrl, {
            method: 'POST',
            body: new FormData(formElement),
            credentials: 'same-origin',
            headers: { Accept: 'text/plain', 'X-CSRF-Token': csrfToken() }
          });
          if (!response.ok) throw new Error('Download unavailable');
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = download.dataset.filename || 'instore-repertoire.txt';
          anchor.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          setPreviewMessage(preview?.dataset.downloadError || error.message);
        } finally {
          download.disabled = false;
          download.removeAttribute('aria-busy');
        }
      });
    }

    form.addEventListener('change', update);
    (formElement || form).addEventListener('submit', event => {
      if (catalogConsent?.checked && (previewState !== 'ready' || !repertoireContext?.value)) {
        event.preventDefault();
        setPreviewMessage(preview?.dataset.pendingMessage || '');
      }
    });
    update();
    if (repertoireEnabled && (!participation || value('pro_status') === 'not_a_pro_member')) requestPreview();
  });
}
